import { type Dispatch, type SetStateAction } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import {
  KpiType,
  type ModelMetadataKpiFormula,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { Checkbox } from "@shared/checkbox";

import {
  PROMO_ID,
  PROMO_LEGEND,
  REVENUE_ID,
  REVENUE_LEGEND,
  ROAS_ID,
  ROAS_LEGEND,
  SPEND_ID,
  SPEND_LEGEND,
  TOTAL_KPI_ID,
  TOTAL_KPI_LEGEND,
} from "./const";

type Props = {
  selectedKpi: ModelMetadataKpiFormula;
  datasetVisibility: Record<string, boolean>;
  setDatasetVisibility: Dispatch<SetStateAction<Record<string, boolean>>>;
};

export function Legend({
  selectedKpi,
  datasetVisibility,
  setDatasetVisibility,
}: Props) {
  const toggleDatasetVisibility =
    (datasetId: keyof typeof datasetVisibility) => (checked: boolean) =>
      setDatasetVisibility((prev: typeof datasetVisibility) => ({
        ...prev,
        [datasetId]: checked,
      }));

  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  return (
    <div className="flex text-xs space-x-4 px-6 pt-4 pb-2">
      <label className="flex items-center space-x-0.5 cursor-pointer">
        <Checkbox
          value={datasetVisibility[SPEND_ID]}
          onChange={toggleDatasetVisibility(SPEND_ID)}
        />
        <div
          className="w-8"
          dangerouslySetInnerHTML={{ __html: SPEND_LEGEND }}
        />
        <div>{SPEND_ID}</div>
      </label>
      <label className="flex items-center space-x-0.5 min-w-0 cursor-pointer">
        <Checkbox
          value={datasetVisibility[TOTAL_KPI_ID]}
          onChange={toggleDatasetVisibility(TOTAL_KPI_ID)}
        />
        <div
          className="w-8"
          dangerouslySetInnerHTML={{ __html: TOTAL_KPI_LEGEND }}
        />
        <span className="truncate">{selectedKpi.friendly_name} (total)</span>
        <GenericTooltip
          content={
            <div>
              Includes the contributions of the channel + the contributions of
              promotions on the channel.
            </div>
          }
        />
      </label>
      <label className="flex items-center space-x-0.5 min-w-0 cursor-pointer">
        <Checkbox
          value={datasetVisibility[REVENUE_ID]}
          onChange={toggleDatasetVisibility(REVENUE_ID)}
        />
        <div
          className="w-8"
          dangerouslySetInnerHTML={{ __html: REVENUE_LEGEND }}
        />
        <span className="truncate">
          {selectedKpi.friendly_name} (channel contributions only)
        </span>
        <GenericTooltip
          content={
            <div>
              Includes just the contributions made by the channel, excluding
              promotions.
            </div>
          }
        />
      </label>
      <label className="flex items-center space-x-0.5 min-w-0 cursor-pointer">
        <Checkbox
          value={datasetVisibility[PROMO_ID]}
          onChange={toggleDatasetVisibility(PROMO_ID)}
        />
        <div
          className="w-8"
          dangerouslySetInnerHTML={{ __html: PROMO_LEGEND }}
        />
        <span className="truncate">
          {selectedKpi.friendly_name} (promotion contributions only)
        </span>
        <GenericTooltip
          content={
            <div>
              Includes just the contributions made by promotions, excluding
              channel contributions.
            </div>
          }
        />
      </label>
      <label className="flex items-center space-x-0.5 cursor-pointer">
        <Checkbox
          value={datasetVisibility[ROAS_ID]}
          onChange={toggleDatasetVisibility(ROAS_ID)}
        />
        <div
          className="w-8"
          dangerouslySetInnerHTML={{ __html: ROAS_LEGEND }}
        />
        <div>i{isRevenue ? "ROAS" : "CAC"}</div>
      </label>
    </div>
  );
}
